"use client";

// BUG-11-CHG-01: stays on raw useAccount + useSignTypedData by design. Login UI hidden when isLoggedIn === true; impersonation requires isLoggedIn === true → login flow cannot trigger during impersonation. See arandao-docs/architecture/impersonation-ux.md §Integration Points exempt list.
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useAccount } from "wagmi";
import { Form } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { CheckIcon, type CheckIconHandle } from "@/components/ui/CheckIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { XIcon, type XIconHandle } from "@/components/ui/XIcon";
import useLogin from "@/hooks/auth/useLogin";
import { $api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { makeTopNavLoginSchema } from "@/lib/validations/auth";
import { PasswordSecondFactorForm } from "./PasswordSecondFactorForm";

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

type NicknameStatus =
  | "idle"
  | "too-short"
  | "checking"
  | "available"
  | "duplicate";

// -------------------------------------------------------------------------
// Props
// -------------------------------------------------------------------------

interface LoginFormProps {
  onCancel?: () => void;
  onSuccess?: () => void;
}
// -------------------------------------------------------------------------
// FormDataSync — syncs wallet address and existing nickname to RHF
// -------------------------------------------------------------------------

function FormDataSync({ nickname }: { nickname?: string | null }) {
  const { setValue } = useFormContext();
  const { address } = useAccount();

  useEffect(() => {
    if (address) {
      setValue("wallet_address", address);
    }
  }, [address, setValue]);

  useEffect(() => {
    if (nickname) {
      setValue("nickname", nickname);
    }
  }, [nickname, setValue]);

  return null;
}

// -------------------------------------------------------------------------
// NicknameField — Controller + Input with live availability check
// -------------------------------------------------------------------------

interface NicknameFieldProps {
  /** When true, the field is locked (user already has a nickname) */
  readOnly: boolean;
  /** Callback so the parent form can track status for submit-button gating */
  onStatusChange: (status: NicknameStatus) => void;
}

function NicknameField({ readOnly, onStatusChange }: NicknameFieldProps) {
  const t = useTranslations("auth");
  const {
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const [nicknameStatus, setNicknameStatus] = useState<NicknameStatus>("idle");

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedValueRef = useRef<string>("");
  const checkIconRef = useRef<CheckIconHandle>(null);
  const xIconRef = useRef<XIconHandle>(null);
  const prevStatusRef = useRef<NicknameStatus>("idle");

  const checkNicknameMutation = $api.useMutation(
    "post",
    "/auth/check-nickname",
  );
  // Keep a stable ref so the debounced callback doesn't close over stale values.
  const checkNicknameMutationRef = useRef(checkNicknameMutation);
  useEffect(() => {
    checkNicknameMutationRef.current = checkNicknameMutation;
  }, [checkNicknameMutation]);

  // Propagate status changes to the parent
  useEffect(() => {
    onStatusChange(nicknameStatus);
  }, [nicknameStatus, onStatusChange]);

  // Animate icons only when transitioning FROM checking state
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    if (prevStatus === "checking") {
      if (nicknameStatus === "available") {
        requestAnimationFrame(() => {
          checkIconRef.current?.startAnimation();
        });
      } else if (nicknameStatus === "duplicate") {
        requestAnimationFrame(() => {
          xIconRef.current?.startAnimation();
        });
      }
    }
    prevStatusRef.current = nicknameStatus;
  }, [nicknameStatus]);

  // Keep the form invalid while checking, duplicate, or too-short (blocks accidental submit)
  useEffect(() => {
    if (nicknameStatus === "checking") {
      setError("nickname", {
        type: "manual",
        message: t("login.checking"),
      });
    } else if (nicknameStatus === "duplicate") {
      setError("nickname", {
        type: "manual",
        message: t("login.taken"),
      });
    } else if (nicknameStatus === "too-short") {
      setError("nickname", {
        type: "manual",
        message: t("login.minTwoChars"),
      });
    }
  }, [nicknameStatus, setError, t]);

  // Watch nickname value and debounce the availability check
  const nicknameValue = watch("nickname");

  useEffect(() => {
    // If the field is read-only (user already has a nickname), never check
    if (readOnly) {
      setNicknameStatus("idle");
      return;
    }

    // Clear any pending timer on each value change
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const trimmedValue = (nicknameValue ?? "").trim();

    // Empty → idle (user hasn't started typing yet — no feedback shown)
    if (trimmedValue.length === 0) {
      setNicknameStatus("idle");
      lastCheckedValueRef.current = "";
      clearErrors("nickname");
      return;
    }

    // Exactly 1 char → too short (invalid, but don't hit the server)
    if (trimmedValue.length < 2) {
      setNicknameStatus("too-short");
      lastCheckedValueRef.current = "";
      return;
    }

    // Already checked this exact value → keep current status
    if (lastCheckedValueRef.current === trimmedValue) {
      return;
    }

    // Show checking state immediately so the submit button is blocked
    setNicknameStatus("checking");

    debounceTimerRef.current = setTimeout(async () => {
      const currentValue = trimmedValue;

      // Guard against stale closure
      if (lastCheckedValueRef.current === currentValue) {
        return;
      }

      try {
        const result = await checkNicknameMutationRef.current.mutateAsync({
          body: { nickname: currentValue },
        });

        lastCheckedValueRef.current = currentValue;

        if (result.data?.is_duplicate) {
          setNicknameStatus("duplicate");
          setError("nickname", {
            type: "manual",
            message: t("login.taken"),
          });
        } else {
          setNicknameStatus("available");
          clearErrors("nickname");
        }
      } catch {
        // Network/server error — reset to idle so submit isn't permanently blocked
        setNicknameStatus("idle");
        lastCheckedValueRef.current = "";
        clearErrors("nickname");
      }
    }, 1500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [nicknameValue, readOnly, setError, clearErrors, t]);

  const renderAdornment = () => (
    <div className="flex items-center justify-center size-5">
      <AnimatePresence mode="wait" initial={false}>
        {nicknameStatus === "checking" && (
          <motion.div
            key="spinner"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <Spinner className="size-5 text-text-secondary" />
          </motion.div>
        )}
        {nicknameStatus === "available" && (
          <motion.div
            key="check"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <CheckIcon
              ref={checkIconRef}
              size={20}
              className="text-semantic-success"
            />
          </motion.div>
        )}
        {(nicknameStatus === "duplicate" || nicknameStatus === "too-short") && (
          <motion.div
            key="x"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center justify-center"
          >
            <XIcon ref={xIconRef} size={20} className="text-semantic-error" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Determine visual error state: show error border + text only on duplicate,
  // not during checking (mirrors BasicInfoForm pattern)
  const error = errors.nickname;
  const showError = !!error && nicknameStatus !== "checking";
  const hasRealError =
    nicknameStatus === "duplicate" ||
    (!!error && nicknameStatus !== "checking");

  return (
    <Controller
      name="nickname"
      control={control}
      rules={{
        validate: () => {
          if (nicknameStatus === "checking") {
            return t("login.checking");
          }
          if (nicknameStatus === "duplicate") {
            return t("login.taken");
          }
          return true;
        },
      }}
      render={({ field }) => (
        <div className="space-y-2 w-full">
          <Label htmlFor="nickname">{t("login.usernameLabel")}</Label>
          <Input
            {...field}
            id="nickname"
            type="text"
            dir="ltr"
            readOnly={readOnly}
            className={cn({
              "border-semantic-error": hasRealError,
              "border-semantic-success": nicknameStatus === "available",
            })}
            endAdornment={!readOnly ? renderAdornment() : undefined}
          />
          {showError && (
            <p className="text-xs leading-tight text-semantic-error ms-1.5">
              {typeof error?.message === "string" ? error.message : null}
            </p>
          )}
        </div>
      )}
    />
  );
}

// -------------------------------------------------------------------------
// LoginForm — main export
// -------------------------------------------------------------------------

export const LoginForm = ({ onCancel, onSuccess }: LoginFormProps) => {
  const t = useTranslations("auth");
  const vt = useTranslations("validation");
  const topNavLoginSchema = useMemo(() => makeTopNavLoginSchema(vt), [vt]);
  const login = useLogin(onSuccess);
  const { address } = useAccount();

  const [nicknameStatus, setNicknameStatus] = useState<NicknameStatus>("idle");

  const user = $api.useQuery(
    "get",
    "/user/wallet/{wallet_address}",
    {
      params: {
        path: { wallet_address: address || "" },
      },
    },
    {
      enabled: !!address,
    },
  );

  const nickname = user.data?.data?.nickname;
  const isReadOnly = !!nickname;

  // Submit is enabled only when the user already has a nickname (readOnly)
  // OR the typed nickname has been verified as available. Every other state
  // (idle/too-short/checking/duplicate) blocks submit.
  const isSubmitBlocked = !isReadOnly && nicknameStatus !== "available";

  if (login.secondFactorChallenge) {
    return <PasswordSecondFactorForm login={login} onCancel={onCancel} />;
  }

  return (
    <Form
      onSubmit={(data) => {
        login.mutate(data);
      }}
      schema={topNavLoginSchema}
      defaultValues={{
        wallet_address: (address || "") as `0x${string}`,
        nickname: nickname || "",
      }}
    >
      <FormDataSync nickname={nickname} />
      {user.isLoading ? <Skeleton className="h-12 w-full" /> : null}
      {!user.isLoading ? (
        <NicknameField
          readOnly={isReadOnly}
          onStatusChange={setNicknameStatus}
        />
      ) : null}

      <p className="text-text-secondary -mt-2 text-xs">
        {t("login.usernameImmutable")}
      </p>

      <div className="flex flex-row-reverse pb-5 gap-3">
        <Button
          loading={login.isPending}
          disabled={isSubmitBlocked}
          type="submit"
          className="w-full"
          variant="solid"
          colors="secondary"
        >
          {t("login.submit")}
        </Button>

        <Button
          type="button"
          className="w-full"
          variant="bordered"
          colors="secondary"
          onClick={onCancel}
        >
          {t("login.cancel")}
        </Button>
      </div>
    </Form>
  );
};
